import type { GroupMonthlyMemo } from "@/components/feature/roster_create/utils/groupMonthlyMemo";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { StickyNote } from "lucide-react";

type MonthlyMemoIndicatorProps = Readonly<{
  nurseName: string;
  memo?: GroupMonthlyMemo;
}>;

function formatMemoUpdatedAt(value: string | null): string | null {
  if (!value) return null;

  const updatedAt = new Date(value);
  if (Number.isNaN(updatedAt.getTime())) return null;

  return new Intl.DateTimeFormat("ko-KR", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(updatedAt);
}

export function MonthlyMemoIndicator({
  nurseName,
  memo,
}: MonthlyMemoIndicatorProps) {
  if (!memo) return null;

  const updatedAtLabel = formatMemoUpdatedAt(memo.updated_at);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="relative inline-flex size-4 shrink-0 items-center justify-center rounded-sm text-amber-700 transition-colors hover:bg-amber-100 focus-visible:ring-2 focus-visible:ring-sig focus-visible:ring-offset-1 dark:text-amber-300 dark:hover:bg-amber-950/60"
          aria-label={`${nurseName} · 월간 메모 있음`}
          onClick={(event) => event.stopPropagation()}
        >
          <StickyNote className="size-3" aria-hidden="true" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        className="z-[70] w-[min(20rem,calc(100vw-2rem))] max-h-56 overflow-y-auto whitespace-normal border border-amber-200 bg-amber-50 px-3 py-2.5 text-slate-900 shadow-lg dark:border-amber-900/70 dark:bg-amber-950/95 dark:text-amber-50"
        side="right"
        sideOffset={8}
        align="center"
        arrowClassName="fill-amber-50 stroke-amber-200 dark:fill-amber-950 dark:stroke-amber-900"
      >
        <p className="text-[10px] font-semibold tracking-wide text-amber-800 dark:text-amber-200">
          {nurseName}의 원티드 메모
        </p>
        <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-5 text-slate-900 dark:text-amber-50">
          {memo.monthly_memo}
        </p>
        <p className="mt-2 border-t border-amber-200/80 pt-1.5 text-[10px] text-amber-800/80 dark:border-amber-900/70 dark:text-amber-200/80">
          작성자 {memo.name}
          {updatedAtLabel ? ` · ${updatedAtLabel}` : ""}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}
